import { useEffect, useRef } from 'react'
import { useEventPlannerStore } from '../store/eventPlannerStore'
import { SIM_MS_PER_REAL_MS_AT_1X } from '../data/eventWindow'
import { driveMotion } from '../motion/motionDriver'

/** Push the timeline clock into the store at ~10Hz during playback (the rate the
 *  timeline scrubber + membership sync need) — not every animation frame. */
const STORE_WRITE_MS = 100

/**
 * Drive the timeline clock while playing.
 *
 * The clock is advanced on a private ref every animation frame and the aircraft
 * are moved imperatively via `driveMotion` (the motion-driver bus), so motion
 * runs at the display refresh rate WITHOUT a React re-render per frame. The
 * Zustand `currentTime` — which every subscribed component re-renders on — is
 * written only every `STORE_WRITE_MS` (and once on reaching the window end), so
 * the React tree updates ~10×/sec instead of 60×/sec. Scrubbing while paused is
 * handled by `useEventActorMotion` reacting to `currentTime` directly.
 */
export function usePlaybackLoop() {
  const playback = useEventPlannerStore((s) => s.playback)
  const rafRef = useRef<number | null>(null)
  const lastFrameRef = useRef(0)
  const liveRef = useRef(0)
  const lastWriteRef = useRef(0)

  useEffect(() => {
    if (playback !== 'playing') return
    // Resume from wherever the store clock currently sits (e.g. after a scrub).
    liveRef.current = useEventPlannerStore.getState().currentTime
    lastFrameRef.current = performance.now()
    lastWriteRef.current = lastFrameRef.current

    const loop = (now: number) => {
      const dt = now - lastFrameRef.current
      lastFrameRef.current = now

      const { speed, windowEnd } = useEventPlannerStore.getState()
      let next = liveRef.current + dt * SIM_MS_PER_REAL_MS_AT_1X * speed
      const ended = next >= windowEnd
      if (ended) next = windowEnd
      liveRef.current = next

      // Imperative 60Hz pose application — does not touch React.
      driveMotion(next)

      // Throttle the store write (drives timeline UI + quantized membership sync).
      if (ended) {
        useEventPlannerStore.setState({ currentTime: windowEnd, playback: 'paused' })
        rafRef.current = null
        return
      }
      if (now - lastWriteRef.current >= STORE_WRITE_MS) {
        lastWriteRef.current = now
        useEventPlannerStore.setState({ currentTime: next })
      }
      rafRef.current = requestAnimationFrame(loop)
    }

    rafRef.current = requestAnimationFrame(loop)
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      // Flush the live clock to the store so pausing mid-frame doesn't snap back
      // to the last throttled write (at high speed that gap is large in sim time).
      const { currentTime, windowEnd } = useEventPlannerStore.getState()
      const flushed = Math.min(liveRef.current, windowEnd)
      if (flushed !== currentTime) useEventPlannerStore.setState({ currentTime: flushed })
    }
  }, [playback])
}
